import React, { useEffect, useMemo, useState } from 'react';
import { fetchChart, peekCache } from './chartCache';

// Reuses the existing ChartDirector-rendered SVG from /chart/:link/:period
// (candles + fundamentals bars stacked in one image) but crops it down to
// just the fundamentals bars beneath the candles - used under the
// TradingView candle chart so that section still comes from ChartDirector
// exactly as before, without re-fetching or re-rendering it separately.
const FundamentalsChart = ({ link, period = 'daily', reloadKey = 0, onClick }) => {
  const [svg, setSvg] = useState('');
  const [plot, setPlot] = useState(null);
  const [naturalSize, setNaturalSize] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!link) return undefined;
    let cancelled = false;
    setError(false);

    const cached = peekCache(link, period, 'full');
    if (cached) {
      setSvg(cached.svg || '');
      setPlot(cached.plot || null);
      const match = /viewBox=['"]0 0 (\d+(?:\.\d+)?) (\d+(?:\.\d+)?)['"]/.exec(cached.svg || '');
      if (match) setNaturalSize({ width: Number(match[1]), height: Number(match[2]) });
    }

    fetchChart(link, period, 'full')
      .then((value) => {
        if (cancelled) return;
        const match = /viewBox=['"]0 0 (\d+(?:\.\d+)?) (\d+(?:\.\d+)?)['"]/.exec(value.svg || '');
        setSvg(value.svg || '');
        setPlot(value.plot || null);
        if (match) setNaturalSize({ width: Number(match[1]), height: Number(match[2]) });
        setError(false);
      })
      .catch(() => { if (!cancelled && !cached) setError(true); });

    return () => { cancelled = true; };
  }, [link, period, reloadKey]);

  const dataUrl = useMemo(() => {
    if (!svg) return '';
    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
  }, [svg]);

  if (!link) return null;
  if (error) return null;
  if (!dataUrl || !plot || !naturalSize) return null;

  // The container's own height comes from padding-top (a percentage of its
  // WIDTH, per spec). But the <img>'s top/height percentages resolve
  // against the container's HEIGHT, not its width - so they need to be
  // expressed relative to the visible (cropped) height, not the natural
  // width, or the image ends up the wrong size within its own crop box.
  const visiblePx = naturalSize.height - plot.financeHeight;
  const visibleHeightPct = (visiblePx / naturalSize.width) * 100;
  const imgHeightPct = (naturalSize.height / visiblePx) * 100;
  const imgTopPct = (plot.financeHeight / visiblePx) * 100;

  return (
    <div className="fundamentals-panel" style={{ paddingTop: `${visibleHeightPct}%` }} onClick={onClick}>
      <img
        className="fundamentals-panel-image"
        src={dataUrl}
        alt=""
        draggable="false"
        title="Click to show/hide the quarterly figures"
        style={{ top: `${-imgTopPct}%`, height: `${imgHeightPct}%`, cursor: onClick ? 'pointer' : 'default' }}
      />
    </div>
  );
};

export default FundamentalsChart;
